import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiClock, FiDownload, FiPrinter, FiRotateCcw, FiAlertTriangle,
  FiCheckCircle, FiArrowUp, FiArrowDown, FiChevronRight, FiChevronDown,
  FiDollarSign, FiX, FiExternalLink
} from 'react-icons/fi';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { colors } from '../../theme/colors';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { recordReads } from '../../utils/readTelemetry';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import VendorSelect from '../../components/VendorSelect';
import { formatCurrency, formatNumber, formatDate } from '../../utils/formatters';
import useCurrency from '../../hooks/useCurrency';
import useReportMemory from '../../hooks/useReportMemory';
import { navigateToTransaction } from '../../utils/navigationHelper';
import { loadPartyLedgerBalances, loadAgingInvoices, allocateBalanceNewestFirst, openingItem, OPENING_ITEM_ID } from '../../utils/ledgerAgingHelper';
import { addPrintBrand } from '../../utils/printBranding';

// ── Constants ──
const CHART_COLORS = ['#800020', '#A0153E', '#5D0017', '#C4A484', '#8B7355', '#D4AF37'];

const AGING_BUCKETS = [
  { key: 'current', label: 'Current (0-30)', min: 0, max: 30 },
  { key: 'days31_60', label: '31-60 Days', min: 31, max: 60 },
  { key: 'days61_90', label: '61-90 Days', min: 61, max: 90 },
  { key: 'days91_120', label: '91-120 Days', min: 91, max: 120 },
  { key: 'days120plus', label: '120+ Days', min: 121, max: Infinity }
];

const BUCKET_COLORS = {
  current: '#28A745',
  days31_60: '#17A2B8',
  days61_90: '#FFC107',
  days91_120: '#FFA000',
  days120plus: '#DC3545'
};

const STACKED_BAR_COLORS = {
  current: '#28A745',
  days31_60: '#17A2B8',
  days61_90: '#D4AF37',
  days91_120: '#A0153E',
  days120plus: '#DC3545'
};

// ── Helpers ──

const formatAmountLocal = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  });
};

const getDaysDifference = (dateStr, asOfDate) => {
  if (!dateStr) return 0;
  const docDate = new Date(dateStr);
  const refDate = new Date(asOfDate);
  docDate.setHours(0, 0, 0, 0);
  refDate.setHours(0, 0, 0, 0);
  const diff = Math.floor((refDate - docDate) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff);
};

const getAgingBucket = (days) => {
  for (const bucket of AGING_BUCKETS) {
    if (days >= bucket.min && days <= bucket.max) {
      return bucket.key;
    }
  }
  return 'days120plus';
};

// ── Component ──

const APAging = () => {
  const navigate = useNavigate();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  // Backward memory: restore filters + generated rows on Back (0 re-reads).
  const [savedMem, saveMemory] = useReportMemory('ap-aging');
  const { formatCurrency } = useCurrency();
  const currencyCode = activeCompany?.currencyCode || 'PKR';
  const currencySymbol = activeCompany?.currencySymbol || 'Rs.';

  // Data states
  const [piTransactions, setPiTransactions] = useState(() => savedMem?.piTransactions ?? []);
  const [paymentTransactions, setPaymentTransactions] = useState(() => savedMem?.paymentTransactions ?? []);
  const [vendors, setVendors] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(() => savedMem?.hasGenerated ?? false);

  // Filter states
  const [selectedFYId, setSelectedFYId] = useState(() => savedMem?.selectedFYId ?? '');
  const [asOfDate, setAsOfDate] = useState(() => savedMem?.asOfDate ?? new Date().toISOString().split('T')[0]);
  const [selectedVendor, setSelectedVendor] = useState(() => savedMem?.selectedVendor ?? 'all');
  const [minBalance, setMinBalance] = useState(() => savedMem?.minBalance ?? '');
  const [taxFilter, setTaxFilter] = useState(() => savedMem?.taxFilter ?? 'both');

  // Sort states
  const [sortColumn, setSortColumn] = useState(() => savedMem?.sortColumn ?? 'totalOutstanding');
  const [sortDirection, setSortDirection] = useState(() => savedMem?.sortDirection ?? 'desc');

  // Detail view state
  const [expandedVendor, setExpandedVendor] = useState(() => savedMem?.expandedVendor ?? null);

  // ── Load Financial Years ──
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(fyRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      recordReads(activeCompany.id, snapshot.size);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);

      if (activeFinancialYear?.id) {
        setSelectedFYId(activeFinancialYear.id);
      } else if (fys.length > 0) {
        setSelectedFYId(fys[0].id);
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear]);

  // ── Load Vendors ──
  const loadVendors = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'vendors');
      const q = query(ref, orderBy('vendorName'));
      const snapshot = await getDocs(q);
      recordReads(activeCompany.id, snapshot.size);
      setVendors(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  }, [activeCompany]);

  // ── Generate Report ──
  const handleGenerateReport = async () => {
    if (!activeCompany?.id) return;

    setLoading(true);
    setHasGenerated(true);
    setExpandedVendor(null);

    try {
      // ACC-09 / ACC-10 fix (2026-09-30): outstanding = the vendor's LEDGER
      // balance as of the date (same as Vendor Balances / Ledger), spread over
      // the vendor's bills newest-first. The old logic subtracted ALL payments
      // ever made from only the still-open bills, so payments for bills already
      // marked Paid were deducted twice and payables were understated.
      const branchId = activeBranch && activeBranch.id !== 'all' ? activeBranch.id : null;
      const [{ balances }, allBills] = await Promise.all([
        loadPartyLedgerBalances(activeCompany.id, { entityType: 'vendor', fyId: selectedFYId, asOfDate, branchId }),
        loadAgingInvoices(activeCompany.id, { codes: ['PI', 'NPI', 'IPI'], fyId: selectedFYId, asOfDate, branchId }),
      ]);
      const fy = financialYears.find(f => f.id === selectedFYId);
      const openingDate = (fy && fy.startDate) || asOfDate;
      const billsByVendor = {};
      allBills.forEach(b => { (billsByVendor[b.partyId] = billsByVendor[b.partyId] || []).push(b); });
      const piData = [];
      Object.keys(balances).forEach(vendorId => {
        const bal = balances[vendorId];
        if (!(bal > 0.009)) return; // settled, or advance paid to vendor
        const { items, openingRemainder } = allocateBalanceNewestFirst(bal, billsByVendor[vendorId] || []);
        if (openingRemainder > 0) piData.push({ ...openingItem(openingRemainder, openingDate), partyId: vendorId, tranTypeCode: 'OB' });
        items.forEach(i => piData.push(i));
      });
      setPiTransactions(piData);
      setPaymentTransactions([]);
    } catch (error) {
      console.error('Error generating AP aging report:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadFinancialYears(); }, [loadFinancialYears]);
  useEffect(() => { loadVendors(); }, [loadVendors]);

  // Remember this view (filters + loaded rows) once generated, so Back restores
  // it with 0 re-reads. Filters here are display-only over already-loaded data,
  // so we keep the snapshot in sync as the user tweaks them (in-memory, ~free).
  useEffect(() => {
    if (!hasGenerated) return;
    saveMemory({
      piTransactions, paymentTransactions, hasGenerated: true,
      selectedFYId, asOfDate, selectedVendor, minBalance, taxFilter,
      sortColumn, sortDirection, expandedVendor
    });
  }, [hasGenerated, piTransactions, paymentTransactions, selectedFYId, asOfDate,
      selectedVendor, minBalance, taxFilter, sortColumn, sortDirection,
      expandedVendor, saveMemory]);

  // ── Build Vendor Map with Aging ──
  const vendorAgingData = useMemo(() => {
    if (!hasGenerated) return {};

    // Apply tax filter to piTransactions
    const filteredPiTransactions = piTransactions.filter(t => {
      if (taxFilter === 'tax') return t.tranTypeCode !== 'NPI';
      if (taxFilter === 'nonTax') return t.tranTypeCode !== 'PI' && t.tranTypeCode !== 'IPI';
      return true;
    });

    // Build vendor invoices map
    const vendorMap = {};
    filteredPiTransactions.forEach(t => {
      if (!t.partyId) return;
      // Only consider invoices on or before as-of date
      if (t.docDate && t.docDate > asOfDate) return;

      if (!vendorMap[t.partyId]) {
        // Try to find vendor details
        const vendorInfo = vendors.find(v => v.id === t.partyId);
        vendorMap[t.partyId] = {
          vendorId: t.partyId,
          vendorName: t.partyName || vendorInfo?.vendorName || 'Unknown',
          vendorCode: vendorInfo?.vendorCode || '-',
          email: vendorInfo?.email || '',
          phone: vendorInfo?.phone || '',
          gstNo: vendorInfo?.gstNo || '',
          paymentTerms: vendorInfo?.paymentTerms || '',
          invoices: [],
          totalInvoiced: 0,
          totalPaid: 0,
          totalOutstanding: 0,
          current: 0,
          days31_60: 0,
          days61_90: 0,
          days91_120: 0,
          days120plus: 0
        };
      }

      const billAmount = t.invoiceAmount ?? (t.netAmount || 0);
      vendorMap[t.partyId].invoices.push({
        id: t.id,
        docNumber: t.docNumber,
        docDate: t.docDate,
        netAmount: billAmount,
        totalAmount: t.totalAmount || 0,
        tranTypeCode: t.tranTypeCode || 'PI',
        ledgerOutstanding: Number(t.outstanding) || 0,
        isOpeningLine: !!t.isOpeningLine
      });
      vendorMap[t.partyId].totalInvoiced += billAmount;
    });

    // For each vendor, age the ledger-based outstanding of each bill (ACC-09)
    Object.keys(vendorMap).forEach(vendorId => {
      const vendor = vendorMap[vendorId];
      const sortedInvoices = [...vendor.invoices].sort((a, b) =>
        (a.docDate || '').localeCompare(b.docDate || '')
      );
      let outstandingTotal = 0;
      sortedInvoices.forEach(inv => {
        inv.outstanding = inv.ledgerOutstanding;
        if (inv.outstanding > 0) {
          outstandingTotal += inv.outstanding;
          const days = getDaysDifference(inv.docDate, asOfDate);
          inv.agingDays = days;
          const bucket = getAgingBucket(days);
          vendor[bucket] += inv.outstanding;
        }
      });
      vendor.totalOutstanding = Math.round(outstandingTotal * 100) / 100;
      vendor.totalPaid = Math.max(0, Math.round((vendor.totalInvoiced - vendor.totalOutstanding) * 100) / 100);
      vendor.invoices = sortedInvoices;
    });

    return vendorMap;
  }, [hasGenerated, piTransactions, vendors, asOfDate, taxFilter]);

  // ── Filtered & Sorted Data ──
  const filteredData = useMemo(() => {
    let data = Object.values(vendorAgingData);

    // Only vendors with outstanding balance
    data = data.filter(v => v.totalOutstanding > 0);

    // Vendor filter
    if (selectedVendor !== 'all') {
      data = data.filter(v => v.vendorId === selectedVendor);
    }

    // Minimum balance filter
    if (minBalance && !isNaN(parseFloat(minBalance))) {
      const threshold = parseFloat(minBalance);
      data = data.filter(v => v.totalOutstanding >= threshold);
    }

    return data;
  }, [vendorAgingData, selectedVendor, minBalance]);

  const sortedData = useMemo(() => {
    const data = [...filteredData];
    data.sort((a, b) => {
      let valA, valB;
      switch (sortColumn) {
        case 'vendorCode':
          valA = (a.vendorCode || '').toLowerCase();
          valB = (b.vendorCode || '').toLowerCase();
          break;
        case 'vendorName':
          valA = (a.vendorName || '').toLowerCase();
          valB = (b.vendorName || '').toLowerCase();
          break;
        case 'current':
          valA = a.current || 0;
          valB = b.current || 0;
          break;
        case 'days31_60':
          valA = a.days31_60 || 0;
          valB = b.days31_60 || 0;
          break;
        case 'days61_90':
          valA = a.days61_90 || 0;
          valB = b.days61_90 || 0;
          break;
        case 'days91_120':
          valA = a.days91_120 || 0;
          valB = b.days91_120 || 0;
          break;
        case 'days120plus':
          valA = a.days120plus || 0;
          valB = b.days120plus || 0;
          break;
        case 'totalOutstanding':
        default:
          valA = a.totalOutstanding || 0;
          valB = b.totalOutstanding || 0;
          break;
      }
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [filteredData, sortColumn, sortDirection]);

  // ── Summary Calculations ──
  const summary = useMemo(() => {
    const totals = {
      totalPayable: 0,
      current: 0,
      days31_60: 0,
      days61_90: 0,
      days91_120: 0,
      days120plus: 0,
      vendorCount: sortedData.length
    };

    sortedData.forEach(v => {
      totals.totalPayable += v.totalOutstanding;
      totals.current += v.current;
      totals.days31_60 += v.days31_60;
      totals.days61_90 += v.days61_90;
      totals.days91_120 += v.days91_120;
      totals.days120plus += v.days120plus;
    });

    return totals;
  }, [sortedData]);

  // ── Chart Data: Stacked Bar - Aging by Vendor (Top 10) ──
  const stackedBarData = useMemo(() => {
    const sorted = [...sortedData]
      .sort((a, b) => (b.totalOutstanding || 0) - (a.totalOutstanding || 0))
      .slice(0, 10);

    return sorted.map(v => ({
      vendor: v.vendorName.length > 16 ? v.vendorName.substring(0, 16) + '...' : v.vendorName,
      'Current': v.current,
      '31-60': v.days31_60,
      '61-90': v.days61_90,
      '91-120': v.days91_120,
      '120+': v.days120plus
    }));
  }, [sortedData]);

  // ── Chart Data: Pie - Payable by Aging Bucket ──
  const pieChartData = useMemo(() => {
    const buckets = [
      { name: 'Current (0-30)', value: summary.current },
      { name: '31-60 Days', value: summary.days31_60 },
      { name: '61-90 Days', value: summary.days61_90 },
      { name: '91-120 Days', value: summary.days91_120 },
      { name: '120+ Days', value: summary.days120plus }
    ];
    return buckets.filter(b => b.value > 0);
  }, [summary]);

  const PIE_BUCKET_COLORS = ['#28A745', '#17A2B8', '#D4AF37', '#A0153E', '#DC3545'];

  // ── Chart Data: Bar - Top 10 Vendors by Outstanding ──
  const topVendorsBarData = useMemo(() => {
    return [...sortedData]
      .sort((a, b) => (b.totalOutstanding || 0) - (a.totalOutstanding || 0))
      .slice(0, 10)
      .map(v => ({
        vendor: v.vendorName.length > 18 ? v.vendorName.substring(0, 18) + '...' : v.vendorName,
        amount: v.totalOutstanding
      }));
  }, [sortedData]);

  // ── Sort Handler ──
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const renderSortIcon = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc'
      ? <FiArrowUp size={11} style={{ marginLeft: 4 }} />
      : <FiArrowDown size={11} style={{ marginLeft: 4 }} />;
  };

  // ── Expand/Collapse Vendor Detail ──
  const handleVendorClick = (vendorId) => {
    setExpandedVendor(prev => prev === vendorId ? null : vendorId);
  };

  // ── Reset Filters ──
  const handleResetFilters = () => {
    setAsOfDate(new Date().toISOString().split('T')[0]);
    setSelectedVendor('all');
    setMinBalance('');
    setExpandedVendor(null);
    if (activeFinancialYear?.id) {
      setSelectedFYId(activeFinancialYear.id);
    }
  };

  // ── CSV Export ──
  const handleExport = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    let csv = 'AP Aging Report\n';
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || 'All'}\n`;
    csv += `As of Date: ${formatDate(asOfDate)}\n`;
    csv += `Total Payable: ${formatAmountLocal(summary.totalPayable)}\n\n`;

    csv += 'Vendor Code,Vendor Name,Current (0-30),31-60 Days,61-90 Days,91-120 Days,120+ Days,Total Outstanding,% of Total\n';

    sortedData.forEach(v => {
      const pct = summary.totalPayable > 0
        ? ((v.totalOutstanding / summary.totalPayable) * 100).toFixed(1)
        : '0.0';
      csv += `"${(v.vendorCode || '').replace(/"/g, '""')}",`;
      csv += `"${(v.vendorName || '').replace(/"/g, '""')}",`;
      csv += `${v.current || 0},`;
      csv += `${v.days31_60 || 0},`;
      csv += `${v.days61_90 || 0},`;
      csv += `${v.days91_120 || 0},`;
      csv += `${v.days120plus || 0},`;
      csv += `${v.totalOutstanding || 0},`;
      csv += `${pct}%\n`;
    });

    // Totals row
    csv += `\n"","TOTAL",`;
    csv += `${summary.current},`;
    csv += `${summary.days31_60},`;
    csv += `${summary.days61_90},`;
    csv += `${summary.days91_120},`;
    csv += `${summary.days120plus},`;
    csv += `${summary.totalPayable},100%\n`;

    // Detail section
    csv += '\n\nDetailed Invoice Breakdown\n';
    csv += 'Vendor,Invoice #,Invoice Date,Invoice Amount,Outstanding,Aging Days,Bucket\n';

    sortedData.forEach(v => {
      v.invoices.forEach(inv => {
        if (inv.outstanding > 0) {
          const bucket = AGING_BUCKETS.find(b => inv.agingDays >= b.min && inv.agingDays <= b.max);
          csv += `"${(v.vendorName || '').replace(/"/g, '""')}",`;
          csv += `"${(inv.docNumber || '').replace(/"/g, '""')}",`;
          csv += `"${formatDate(inv.docDate)}",`;
          csv += `${inv.netAmount || 0},`;
          csv += `${inv.outstanding || 0},`;
          csv += `${inv.agingDays || 0},`;
          csv += `"${bucket?.label || 'Unknown'}"\n`;
        }
      });
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `AP_Aging_${asOfDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // ── Print ──
  const handlePrint = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>AP Aging Report</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:13px}
        h1{color:#800020;font-size:20px;margin-bottom:5px}
        .meta{color:#666;font-size:12px;margin-bottom:20px}
        .summary-row{display:flex;gap:20px;margin-bottom:20px;flex-wrap:wrap}
        .summary-box{border:1px solid #ddd;border-radius:6px;padding:10px 16px;min-width:140px}
        .summary-box .label{font-size:11px;color:#666;text-transform:uppercase}
        .summary-box .value{font-size:18px;font-weight:600;color:#1A1A1A}
        table{width:100%;border-collapse:collapse;margin-top:10px}
        th{background:#800020;color:white;padding:8px 10px;text-align:left;font-size:11px;text-transform:uppercase}
        th.right{text-align:right}
        td{padding:6px 10px;border-bottom:1px solid #eee;font-size:12px}
        td.right{text-align:right;font-family:'Consolas',monospace}
        .totals-row{background:#f5f5f5;font-weight:700}
        .totals-row td{border-top:2px solid #800020;padding:10px}
        .aging-current{color:#28A745}
        .aging-31{color:#17A2B8}
        .aging-61{color:#FFA000}
        .aging-91{color:#A0153E}
        .aging-120{color:#DC3545;font-weight:600}
        @media print{body{margin:15px}}
      </style></head><body>
      <h1>Accounts Payable Aging Report</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || 'All'} |
        As of Date: ${formatDate(asOfDate)} |
        Vendors: ${sortedData.length}
      </div>
      <div class="summary-row">
        <div class="summary-box"><div class="label">Total Payable</div><div class="value">${currencySymbol}${formatAmountLocal(summary.totalPayable)}</div></div>
        <div class="summary-box"><div class="label">Current (0-30)</div><div class="value" style="color:#28A745">${currencySymbol}${formatAmountLocal(summary.current)}</div></div>
        <div class="summary-box"><div class="label">31-60 Days</div><div class="value" style="color:#17A2B8">${currencySymbol}${formatAmountLocal(summary.days31_60)}</div></div>
        <div class="summary-box"><div class="label">61-90 Days</div><div class="value" style="color:#FFA000">${currencySymbol}${formatAmountLocal(summary.days61_90)}</div></div>
        <div class="summary-box"><div class="label">91-120 Days</div><div class="value" style="color:#A0153E">${currencySymbol}${formatAmountLocal(summary.days91_120)}</div></div>
        <div class="summary-box"><div class="label">120+ Days</div><div class="value" style="color:#DC3545">${currencySymbol}${formatAmountLocal(summary.days120plus)}</div></div>
      </div>
      <table>
        <thead><tr>
          <th>Code</th><th>Vendor Name</th>
          <th class="right">Current (0-30)</th><th class="right">31-60</th>
          <th class="right">61-90</th><th class="right">91-120</th>
          <th class="right">120+</th><th class="right">Total Outstanding</th>
          <th class="right">%</th>
        </tr></thead>
        <tbody>`);

    sortedData.forEach(v => {
      const pct = summary.totalPayable > 0
        ? ((v.totalOutstanding / summary.totalPayable) * 100).toFixed(1)
        : '0.0';
      w.document.write(`
        <tr>
          <td>${v.vendorCode || '-'}</td>
          <td>${v.vendorName || ''}</td>
          <td class="right aging-current">${formatAmountLocal(v.current)}</td>
          <td class="right aging-31">${formatAmountLocal(v.days31_60)}</td>
          <td class="right aging-61">${formatAmountLocal(v.days61_90)}</td>
          <td class="right aging-91">${formatAmountLocal(v.days91_120)}</td>
          <td class="right aging-120">${formatAmountLocal(v.days120plus)}</td>
          <td class="right" style="font-weight:600">${formatAmountLocal(v.totalOutstanding)}</td>
          <td class="right">${pct}%</td>
        </tr>`);
    });

    const totalPct = summary.totalPayable > 0 ? '100.0' : '0.0';
    w.document.write(`
        <tr class="totals-row">
          <td colspan="2" style="text-align:right">TOTAL (${sortedData.length} vendors)</td>
          <td class="right">${formatAmountLocal(summary.current)}</td>
          <td class="right">${formatAmountLocal(summary.days31_60)}</td>
          <td class="right">${formatAmountLocal(summary.days61_90)}</td>
          <td class="right">${formatAmountLocal(summary.days91_120)}</td>
          <td class="right">${formatAmountLocal(summary.days120plus)}</td>
          <td class="right">${formatAmountLocal(summary.totalPayable)}</td>
          <td class="right">${totalPct}%</td>
        </tr>
        </tbody></table>
        <div style="margin-top:30px;text-align:center;color:#999;font-size:11px">
          Generated on ${new Date().toLocaleString()} | AMAL ERP
        </div>
      </body></html>`);
    addPrintBrand(w);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  // ── Custom Tooltip for Charts ──
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <ChartTooltipWrapper>
        <ChartTooltipLabel>{label}</ChartTooltipLabel>
        {payload.map((entry, i) => (
          <ChartTooltipRow key={i}>
            <ChartTooltipDot style={{ background: entry.color || entry.fill }} />
            <span>{entry.name}:</span>
            <strong>{formatCurrency(entry.value)}</strong>
          </ChartTooltipRow>
        ))}
      </ChartTooltipWrapper>
    );
  };

  const PieCustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0];
    const total = pieChartData.reduce((s, d) => s + d.value, 0);
    const pct = total > 0 ? ((data.value / total) * 100).toFixed(1) : 0;
    return (
      <ChartTooltipWrapper>
        <ChartTooltipLabel>{data.name}</ChartTooltipLabel>
        <ChartTooltipRow>
          <span>Amount:</span>
          <strong>{formatCurrency(data.value)}</strong>
        </ChartTooltipRow>
        <ChartTooltipRow>
          <span>Share:</span>
          <strong>{pct}%</strong>
        </ChartTooltipRow>
      </ChartTooltipWrapper>
    );
  };

  // ── Get percentage for bucket ──
  const getBucketPct = (bucketValue) => {
    if (!summary.totalPayable || summary.totalPayable === 0) return '0.0';
    return ((bucketValue / summary.totalPayable) * 100).toFixed(1);
  };

  // ── Render ──
  return (
    <PageLayout pageTitle="AP Aging" breadcrumb="Reports / Purchase Reports / AP Aging">
      {/* Summary Cards */}
      {hasGenerated && !loading && (
      <CardsGrid>
        <SummaryCard>
          <CardIcon color={colors.primary.main}>
            <FiDollarSign />
          </CardIcon>
          <CardInfo>
            <CardLabel>Total Payable</CardLabel>
            <CardValue>{formatCurrency(summary.totalPayable)}</CardValue>
            <CardSub>{summary.vendorCount} vendor{summary.vendorCount !== 1 ? 's' : ''}</CardSub>
          </CardInfo>
        </SummaryCard>

        <SummaryCard>
          <CardIcon color="#28A745">
            <FiCheckCircle />
          </CardIcon>
          <CardInfo>
            <CardLabel>Current (0-30)</CardLabel>
            <CardValue style={{ color: '#28A745' }}>{formatCurrency(summary.current)}</CardValue>
            <CardSub>{getBucketPct(summary.current)}% of total</CardSub>
          </CardInfo>
        </SummaryCard>

        <SummaryCard>
          <CardIcon color="#17A2B8">
            <FiClock />
          </CardIcon>
          <CardInfo>
            <CardLabel>31-60 Days</CardLabel>
            <CardValue style={{ color: '#17A2B8' }}>{formatCurrency(summary.days31_60)}</CardValue>
            <CardSub>{getBucketPct(summary.days31_60)}% of total</CardSub>
          </CardInfo>
        </SummaryCard>

        <SummaryCard>
          <CardIcon color="#FFC107">
            <FiClock />
          </CardIcon>
          <CardInfo>
            <CardLabel>61-90 Days</CardLabel>
            <CardValue style={{ color: '#FFA000' }}>{formatCurrency(summary.days61_90)}</CardValue>
            <CardSub>{getBucketPct(summary.days61_90)}% of total</CardSub>
          </CardInfo>
        </SummaryCard>

        <SummaryCard $danger>
          <CardIcon color="#DC3545">
            <FiAlertTriangle />
          </CardIcon>
          <CardInfo>
            <CardLabel>90+ Days</CardLabel>
            <CardValue style={{ color: '#DC3545' }}>
              {formatCurrency((summary.days91_120 || 0) + (summary.days120plus || 0))}
            </CardValue>
            <CardSub>{getBucketPct((summary.days91_120 || 0) + (summary.days120plus || 0))}% of total</CardSub>
          </CardInfo>
        </SummaryCard>
      </CardsGrid>
      )}

      {/* Filters */}
      <FiltersCard>
        <FiltersRow>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select
              value={selectedFYId}
              onChange={(e) => setSelectedFYId(e.target.value)}
            >
              <option value="">-- Select Year --</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>As of Date</Label>
            <DateInput
              type="date"
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Vendor</Label>
            <VendorSelect
              vendors={vendors}
              selectedVendorId={selectedVendor}
              onChange={setSelectedVendor}
              allOption={{ value: 'all', label: 'All Vendors' }}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Min. Balance</Label>
            <NumberInput
              type="number"
              placeholder="0"
              value={minBalance}
              onChange={(e) => setMinBalance(e.target.value)}
              min="0"
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Tax Filter</Label>
            <Select value={taxFilter} onChange={(e) => setTaxFilter(e.target.value)}>
              <option value="both">Both (Tax + Non-Tax)</option>
              <option value="tax">Tax Only</option>
              <option value="nonTax">Non-Tax Only</option>
            </Select>
          </FilterGroup>

          <ButtonGroup>
            <Button onClick={handleResetFilters} title="Reset Filters">
              <FiRotateCcw /> Reset
            </Button>
            <Button onClick={handleExport} disabled={sortedData.length === 0}>
              <FiDownload /> CSV
            </Button>
            <Button onClick={handlePrint} disabled={sortedData.length === 0}>
              <FiPrinter /> Print
            </Button>
            <Button primary onClick={handleGenerateReport} disabled={loading || !selectedFYId}>
              <FiClock /> {loading ? 'Loading...' : 'Generate'}
            </Button>
          </ButtonGroup>
        </FiltersRow>
      </FiltersCard>

      {/* Data Table */}
      <ContentCard>
        <TableHeader>
          <TableHeaderTitle>
            Vendor Aging Details
            <TableCount>{sortedData.length} vendor{sortedData.length !== 1 ? 's' : ''}</TableCount>
          </TableHeaderTitle>
          {summary.totalPayable > 0 && (
            <AgingBarContainer>
              <AgingBarSegment
                $width={getBucketPct(summary.current)}
                $color="#28A745"
                title={`Current: ${getBucketPct(summary.current)}%`}
              />
              <AgingBarSegment
                $width={getBucketPct(summary.days31_60)}
                $color="#17A2B8"
                title={`31-60: ${getBucketPct(summary.days31_60)}%`}
              />
              <AgingBarSegment
                $width={getBucketPct(summary.days61_90)}
                $color="#D4AF37"
                title={`61-90: ${getBucketPct(summary.days61_90)}%`}
              />
              <AgingBarSegment
                $width={getBucketPct(summary.days91_120)}
                $color="#A0153E"
                title={`91-120: ${getBucketPct(summary.days91_120)}%`}
              />
              <AgingBarSegment
                $width={getBucketPct(summary.days120plus)}
                $color="#DC3545"
                title={`120+: ${getBucketPct(summary.days120plus)}%`}
              />
            </AgingBarContainer>
          )}
        </TableHeader>
        <TableContainer>
          {loading ? (
            <EmptyState>
              <LoadingSpinner />
              <EmptyText>Generating AP aging report...</EmptyText>
            </EmptyState>
          ) : !hasGenerated ? (
            <EmptyState>
              <EmptyIcon><FiClock /></EmptyIcon>
              <EmptyText>Select a financial year and click Generate to view the AP aging report</EmptyText>
            </EmptyState>
          ) : sortedData.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiClock /></EmptyIcon>
              <EmptyText>
                {piTransactions.length === 0
                  ? 'No purchase invoices found for this financial year'
                  : 'No outstanding payables match the selected filters'}
              </EmptyText>
              <EmptySubText>Try adjusting your filters or selecting a different financial year</EmptySubText>
            </EmptyState>
          ) : (
            <Table>
              <Thead>
                <tr>
                  <Th style={{ width: 30 }} />
                  <Th sortable onClick={() => handleSort('vendorCode')}>
                    Code {renderSortIcon('vendorCode')}
                  </Th>
                  <Th sortable onClick={() => handleSort('vendorName')}>
                    Vendor Name {renderSortIcon('vendorName')}
                  </Th>
                  <AgingTh $color="#28A745" align="right" sortable onClick={() => handleSort('current')}>
                    Current (0-30) {renderSortIcon('current')}
                  </AgingTh>
                  <AgingTh $color="#17A2B8" align="right" sortable onClick={() => handleSort('days31_60')}>
                    31-60 {renderSortIcon('days31_60')}
                  </AgingTh>
                  <AgingTh $color="#D4AF37" align="right" sortable onClick={() => handleSort('days61_90')}>
                    61-90 {renderSortIcon('days61_90')}
                  </AgingTh>
                  <AgingTh $color="#A0153E" align="right" sortable onClick={() => handleSort('days91_120')}>
                    91-120 {renderSortIcon('days91_120')}
                  </AgingTh>
                  <AgingTh $color="#DC3545" align="right" sortable onClick={() => handleSort('days120plus')}>
                    120+ {renderSortIcon('days120plus')}
                  </AgingTh>
                  <Th align="right" sortable onClick={() => handleSort('totalOutstanding')}>
                    Total Outstanding {renderSortIcon('totalOutstanding')}
                  </Th>
                  <Th align="right">%</Th>
                </tr>
              </Thead>
              <Tbody>
                {sortedData.map(v => {
                  const pct = summary.totalPayable > 0
                    ? ((v.totalOutstanding / summary.totalPayable) * 100).toFixed(1)
                    : '0.0';
                  const isExpanded = expandedVendor === v.vendorId;
                  const unpaidInvoices = v.invoices.filter(inv => inv.outstanding > 0);

                  return (
                    <React.Fragment key={v.vendorId}>
                      <Tr
                        onClick={() => handleVendorClick(v.vendorId)}
                        style={{ cursor: 'pointer' }}
                      >
                        <Td style={{ width: 30, paddingRight: 0 }}>
                          <ExpandIcon $expanded={isExpanded}>
                            {isExpanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                          </ExpandIcon>
                        </Td>
                        <Td>
                          <VendorCode>{v.vendorCode || '-'}</VendorCode>
                        </Td>
                        <Td title={v.vendorName}>
                          <VendorNameCell>{v.vendorName}</VendorNameCell>
                        </Td>
                        <AgingAmountTd $color="#28A745" align="right">
                          {v.current > 0 ? formatAmountLocal(v.current) : '-'}
                        </AgingAmountTd>
                        <AgingAmountTd $color="#17A2B8" align="right">
                          {v.days31_60 > 0 ? formatAmountLocal(v.days31_60) : '-'}
                        </AgingAmountTd>
                        <AgingAmountTd $color="#D4AF37" align="right">
                          {v.days61_90 > 0 ? formatAmountLocal(v.days61_90) : '-'}
                        </AgingAmountTd>
                        <AgingAmountTd $color="#A0153E" align="right">
                          {v.days91_120 > 0 ? formatAmountLocal(v.days91_120) : '-'}
                        </AgingAmountTd>
                        <AgingAmountTd $color="#DC3545" $bold align="right">
                          {v.days120plus > 0 ? formatAmountLocal(v.days120plus) : '-'}
                        </AgingAmountTd>
                        <AmountTd align="right" $highlight>
                          {formatAmountLocal(v.totalOutstanding)}
                        </AmountTd>
                        <AmountTd align="right">
                          <PctBadge>{pct}%</PctBadge>
                        </AmountTd>
                      </Tr>

                      {/* Expanded Detail View */}
                      {isExpanded && (
                        <DetailRow>
                          <td colSpan={10}>
                            <DetailContainer>
                              <DetailHeader>
                                <DetailTitle>
                                  Outstanding Invoices for {v.vendorName}
                                  {v.paymentTerms && (
                                    <DetailBadge>Terms: {v.paymentTerms}</DetailBadge>
                                  )}
                                </DetailTitle>
                                <DetailMeta>
                                  {v.email && <span>{v.email}</span>}
                                  {v.phone && <span>{v.phone}</span>}
                                  {v.gstNo && <span>{activeCompany?.taxLabel || 'GST'}: {v.gstNo}</span>}
                                  <ActionLinkBtn
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate('/payments/cash-payment', {
                                        state: {
                                          prefill: {
                                            partyId: v.vendorId,
                                            partyName: v.vendorName,
                                            linkedAccountId: v.linkedAccountId || null
                                          }
                                        }
                                      });
                                    }}
                                  >
                                    <FiDollarSign size={12} /> Make Payment
                                  </ActionLinkBtn>
                                </DetailMeta>
                              </DetailHeader>
                              <DetailTable>
                                <thead>
                                  <tr>
                                    <DetailTh>Invoice #</DetailTh>
                                    <DetailTh>Invoice Date</DetailTh>
                                    <DetailTh align="right">Invoice Amount</DetailTh>
                                    <DetailTh align="right">Outstanding</DetailTh>
                                    <DetailTh align="right">Days</DetailTh>
                                    <DetailTh>Aging Bucket</DetailTh>
                                  </tr>
                                </thead>
                                <tbody>
                                  {unpaidInvoices.length > 0 ? unpaidInvoices.map(inv => {
                                    const bucket = AGING_BUCKETS.find(
                                      b => (inv.agingDays || 0) >= b.min && (inv.agingDays || 0) <= b.max
                                    );
                                    const bucketKey = bucket ? bucket.key : 'days120plus';
                                    return (
                                      <DetailTr key={inv.id}>
                                        <DetailTd>
                                          <DocLink
                                            onClick={(e) => { e.stopPropagation(); if (inv.id === OPENING_ITEM_ID) return; navigateToTransaction(navigate, inv.tranTypeCode || 'PI', inv.id); }}
                                            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                          >
                                            {inv.docNumber || '-'} <FiExternalLink size={11} style={{ marginLeft: 3, verticalAlign: 'middle' }} />
                                          </DocLink>
                                        </DetailTd>
                                        <DetailTd>{formatDate(inv.docDate)}</DetailTd>
                                        <DetailTd align="right" style={{ fontFamily: "'SF Mono','Consolas',monospace", fontSize: 12 }}>
                                          {formatAmountLocal(inv.netAmount)}
                                        </DetailTd>
                                        <DetailTd align="right" style={{ fontFamily: "'SF Mono','Consolas',monospace", fontSize: 12, fontWeight: 600 }}>
                                          {formatAmountLocal(inv.outstanding)}
                                        </DetailTd>
                                        <DetailTd align="right" style={{ fontFamily: "'SF Mono','Consolas',monospace", fontSize: 12 }}>
                                          {inv.agingDays || 0}
                                        </DetailTd>
                                        <DetailTd>
                                          <AgingBucketBadge $color={BUCKET_COLORS[bucketKey]}>
                                            {bucket?.label || '120+ Days'}
                                          </AgingBucketBadge>
                                        </DetailTd>
                                      </DetailTr>
                                    );
                                  }) : (
                                    <DetailTr>
                                      <DetailTd colSpan={6} style={{ textAlign: 'center', color: colors.text.tertiary }}>
                                        No outstanding invoices
                                      </DetailTd>
                                    </DetailTr>
                                  )}
                                </tbody>
                              </DetailTable>
                              <DetailSummary>
                                <DetailSummaryItem>
                                  <span>Total Invoiced:</span>
                                  <strong>{formatCurrency(v.totalInvoiced)}</strong>
                                </DetailSummaryItem>
                                <DetailSummaryItem>
                                  <span>Total Paid:</span>
                                  <strong style={{ color: '#28A745' }}>{formatCurrency(v.totalPaid)}</strong>
                                </DetailSummaryItem>
                                <DetailSummaryItem>
                                  <span>Outstanding:</span>
                                  <strong style={{ color: '#DC3545' }}>{formatCurrency(v.totalOutstanding)}</strong>
                                </DetailSummaryItem>
                              </DetailSummary>
                            </DetailContainer>
                          </td>
                        </DetailRow>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Totals Row */}
                <TotalsRow>
                  <Td colSpan={3} style={{ textAlign: 'right', fontWeight: 700 }}>
                    TOTAL ({sortedData.length} vendors)
                  </Td>
                  <AgingAmountTd $color="#28A745" align="right">
                    {formatAmountLocal(summary.current)}
                  </AgingAmountTd>
                  <AgingAmountTd $color="#17A2B8" align="right">
                    {formatAmountLocal(summary.days31_60)}
                  </AgingAmountTd>
                  <AgingAmountTd $color="#D4AF37" align="right">
                    {formatAmountLocal(summary.days61_90)}
                  </AgingAmountTd>
                  <AgingAmountTd $color="#A0153E" align="right">
                    {formatAmountLocal(summary.days91_120)}
                  </AgingAmountTd>
                  <AgingAmountTd $color="#DC3545" $bold align="right">
                    {formatAmountLocal(summary.days120plus)}
                  </AgingAmountTd>
                  <AmountTd align="right" $highlight>
                    {formatAmountLocal(summary.totalPayable)}
                  </AmountTd>
                  <AmountTd align="right">
                    <PctBadge>100%</PctBadge>
                  </AmountTd>
                </TotalsRow>
              </Tbody>
            </Table>
          )}
        </TableContainer>
      </ContentCard>

      {/* Charts */}
      {hasGenerated && !loading && sortedData.length > 0 && (
        <ChartsGrid>
          {/* Stacked Bar: Aging by Vendor (Top 10) */}
          <ChartCard $span={2}>
            <ChartTitle>Aging Breakdown by Vendor (Top 10)</ChartTitle>
            {stackedBarData.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={stackedBarData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E9ECEF" />
                  <XAxis
                    dataKey="vendor"
                    tick={{ fontSize: 10, fill: colors.text.secondary }}
                    axisLine={{ stroke: '#DEE2E6' }}
                    tickLine={false}
                    interval={0}
                    angle={-25}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: colors.text.secondary }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => formatCurrency(v, { compact: true })}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Current" stackId="aging" fill={STACKED_BAR_COLORS.current} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="31-60" stackId="aging" fill={STACKED_BAR_COLORS.days31_60} />
                  <Bar dataKey="61-90" stackId="aging" fill={STACKED_BAR_COLORS.days61_90} />
                  <Bar dataKey="91-120" stackId="aging" fill={STACKED_BAR_COLORS.days91_120} />
                  <Bar dataKey="120+" stackId="aging" fill={STACKED_BAR_COLORS.days120plus} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No aging data available</ChartEmpty>
            )}
          </ChartCard>

          {/* Pie Chart: Payable by Aging Bucket */}
          <ChartCard>
            <ChartTitle>Payable by Aging Bucket</ChartTitle>
            {pieChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={100}
                    paddingAngle={3}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) =>
                      `${name.length > 14 ? name.substring(0, 14) + '..' : name} (${(percent * 100).toFixed(0)}%)`
                    }
                    labelLine={{ stroke: colors.text.secondary, strokeWidth: 1 }}
                  >
                    {pieChartData.map((entry, idx) => {
                      const colorIdx = AGING_BUCKETS.findIndex(b => b.label === entry.name);
                      return (
                        <Cell
                          key={idx}
                          fill={PIE_BUCKET_COLORS[colorIdx >= 0 ? colorIdx : idx % PIE_BUCKET_COLORS.length]}
                        />
                      );
                    })}
                  </Pie>
                  <Tooltip content={<PieCustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No aging data available</ChartEmpty>
            )}
          </ChartCard>

          {/* Bar Chart: Top 10 Vendors by Outstanding */}
          <ChartCard>
            <ChartTitle>Top 10 Vendors by Outstanding</ChartTitle>
            {topVendorsBarData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topVendorsBarData} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E9ECEF" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 10, fill: colors.text.secondary }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => formatCurrency(v, { compact: true })}
                  />
                  <YAxis
                    type="category"
                    dataKey="vendor"
                    tick={{ fontSize: 10, fill: colors.text.secondary }}
                    axisLine={false}
                    tickLine={false}
                    width={130}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="amount" name="Outstanding" fill="#800020" radius={[0, 4, 4, 0]} barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No vendor data available</ChartEmpty>
            )}
          </ChartCard>
        </ChartsGrid>
      )}
    </PageLayout>
  );
};

// ══════════════════════════════════════════════════════════════
//  Styled Components
// ══════════════════════════════════════════════════════════════

// ── Summary Cards ──

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  border-left: ${props => props.$danger ? '4px solid #DC3545' : 'none'};

  &:hover {
    box-shadow: ${colors.shadows.md};
    transform: translateY(-1px);
  }
`;

const CardIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.color || colors.primary.main}15;
  color: ${props => props.color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const CardInfo = styled.div`
  min-width: 0;
  flex: 1;
`;

const CardLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
  text-transform: uppercase;
  font-weight: 600;
`;

const CardValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${colors.text.primary};
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardSub = styled.div`
  font-size: 11px;
  color: ${colors.text.tertiary};
  margin-top: 2px;
`;

// ── Filters ──

const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 160px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 0;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const DateInput = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  transition: all 0.2s;
  min-width: 140px;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const NumberInput = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  transition: all 0.2s;
  min-width: 100px;
  max-width: 140px;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    opacity: 1;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`;

const Button = styled.button`
  padding: 9px 16px;
  border: none;
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  white-space: nowrap;

  ${props => props.primary ? `
    background: ${colors.primary.main};
    color: white;
    &:hover:not(:disabled) {
      background: ${colors.primary.dark};
    }
  ` : `
    background: ${colors.neutral[100]};
    color: ${colors.text.primary};
    &:hover:not(:disabled) {
      background: ${colors.neutral[200]};
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ── Charts ──

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  grid-column: ${props => props.$span ? `span ${props.$span}` : 'span 1'};

  @media (max-width: 1024px) {
    grid-column: span 1;
  }
`;

const ChartTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 16px;
`;

const ChartEmpty = styled.div`
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.text.tertiary};
  font-size: 14px;
`;

const ChartTooltipWrapper = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${colors.borderRadius.md};
  box-shadow: ${colors.shadows.md};
  padding: 10px 14px;
  font-size: 12px;
`;

const ChartTooltipLabel = styled.div`
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 6px;
  font-size: 12px;
`;

const ChartTooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${colors.text.secondary};
  margin-bottom: 2px;

  strong {
    color: ${colors.text.primary};
    margin-left: auto;
  }
`;

const ChartTooltipDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
`;

// ── Table ──

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${colors.neutral[100]};
  gap: 16px;
  flex-wrap: wrap;
`;

const TableHeaderTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.text.primary};
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TableCount = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${colors.text.secondary};
  background: ${colors.neutral[100]};
  padding: 3px 10px;
  border-radius: 12px;
`;

const AgingBarContainer = styled.div`
  display: flex;
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
  flex: 1;
  max-width: 400px;
  min-width: 200px;
  background: ${colors.neutral[100]};
`;

const AgingBarSegment = styled.div`
  height: 100%;
  width: ${props => props.$width || 0}%;
  background: ${props => props.$color || colors.primary.main};
  transition: width 0.3s ease;
  min-width: ${props => parseFloat(props.$width) > 0 ? '2px' : '0'};
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 11px 14px;
  text-align: ${props => props.align || 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid ${colors.neutral[200]};
  white-space: nowrap;
  user-select: none;

  ${props => props.sortable && `
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: ${colors.primary.main};
    }
  `}
`;

const AgingTh = styled(Th)`
  border-bottom: 3px solid ${props => props.$color || colors.neutral[300]};
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.15s;

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const TotalsRow = styled.tr`
  background: ${colors.primary.main}08;
  border-top: 2px solid ${colors.primary.main};

  td {
    font-weight: 700 !important;
    font-size: 13px !important;
    padding: 12px 14px !important;
  }
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: ${props => props.align || 'left'};
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AmountTd = styled(Td)`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 12px;
  color: ${props => props.$highlight ? colors.text.primary : colors.text.secondary};
  font-weight: ${props => props.$highlight ? 600 : 400};
`;

const AgingAmountTd = styled(AmountTd)`
  color: ${props => props.$color || colors.text.secondary};
  font-weight: ${props => props.$bold ? 600 : 400};
`;

const VendorCode = styled.span`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 12px;
  color: ${colors.text.secondary};
`;

const VendorNameCell = styled.span`
  font-weight: 500;
  color: ${colors.primary.main};
`;

const DocLink = styled.span`
  color: ${colors.primary.main};
  font-weight: 600;
  cursor: pointer;
  font-size: 12px;
  font-family: 'SF Mono', 'Consolas', monospace;
  transition: color 0.2s;

  &:hover {
    color: ${colors.primary.dark};
    text-decoration: underline;
  }
`;

const ExpandIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$expanded ? colors.primary.main : colors.text.tertiary};
  transition: color 0.2s;
`;

const PctBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  background: ${colors.neutral[100]};
  color: ${colors.text.secondary};
  font-family: 'SF Mono', 'Consolas', monospace;
`;

// ── Detail View (Expanded Row) ──

const DetailRow = styled.tr`
  background: ${colors.neutral[50]};

  > td {
    padding: 0 !important;
  }
`;

const DetailContainer = styled.div`
  padding: 16px 24px 20px;
  margin: 0 14px 8px;
  border-left: 3px solid ${colors.primary.main};
  background: white;
  border-radius: 0 ${colors.borderRadius.md} ${colors.borderRadius.md} 0;
`;

const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
`;

const DetailTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.text.primary};
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DetailBadge = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${colors.primary.main};
  background: ${colors.primary.main}12;
  padding: 3px 10px;
  border-radius: 4px;
`;

const DetailMeta = styled.div`
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: ${colors.text.tertiary};
  align-items: center;
  flex-wrap: wrap;

  span {
    display: flex;
    align-items: center;
    gap: 4px;
  }
`;

const ActionLinkBtn = styled.button`
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 12px;
  background: ${colors.error.main};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 12px; font-weight: 600; cursor: pointer;
  &:hover { opacity: 0.9; }
`;

const DetailTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 12px;
`;

const DetailTh = styled.th`
  padding: 8px 12px;
  text-align: ${props => props.align || 'left'};
  font-size: 10px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid ${colors.neutral[200]};
  background: ${colors.neutral[50]};
`;

const DetailTr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const DetailTd = styled.td`
  padding: 7px 12px;
  font-size: 12px;
  color: ${colors.text.primary};
  text-align: ${props => props.align || 'left'};
  white-space: nowrap;
`;

const AgingBucketBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  background: ${props => props.$color || colors.neutral[400]}18;
  color: ${props => props.$color || colors.text.secondary};
`;

const DetailSummary = styled.div`
  display: flex;
  gap: 24px;
  padding-top: 10px;
  border-top: 1px solid ${colors.neutral[200]};
`;

const DetailSummaryItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${colors.text.secondary};

  strong {
    font-weight: 600;
    color: ${colors.text.primary};
  }
`;

// ── Empty / Loading ──

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 6px;
  color: ${colors.text.primary};
`;

const EmptySubText = styled.p`
  font-size: 13px;
  color: ${colors.text.tertiary};
`;

const LoadingSpinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid ${colors.neutral[200]};
  border-top: 3px solid ${colors.primary.main};
  border-radius: 50%;
  margin: 0 auto 16px auto;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

export default APAging;
